import { isNil, keyBy } from 'lodash';
import { SetNonNullable, SetRequired } from 'type-fest';

import { TileFontSizeVariant } from '@/components/diagrams/components/Tile';
import { Node, TileNode } from '@/components/diagrams/FlowChart';
import { getBoundingBoxForNodes } from '@/components/diagrams/FlowChart/utils/nodes';
import { AfterDeath } from '@/types/schema';

import {
  createTextNoteNodeFromConfig,
  getTextNoteConfigs,
} from '../../../components/diagrams/FlowChart/utils/textNoteUtils';
import { GraphViz_NodeConfigFragment } from '../graphql/GraphVizNodeConfig.fragment.generated';
import {
  GraphVizNodeComponentType,
  parseIconShape,
  parseTileEmphasis,
} from './constants';
import {
  isNodeEligibleForGraveyard,
  transformNodeForNodeGraveyard,
} from './nodeGraveyard.utils';

type NonGraveyardNodeConfig = SetNonNullable<
  SetRequired<GraphViz_NodeConfigFragment, 'xPosition' | 'yPosition'>,
  'xPosition' | 'yPosition'
>;

/**
 * @description A position of 0 is equivalent to a layout that has not been yet auto-layouted,
 * in which case we take the existing node position in the graph
 * (implies the config was saved with a non-autolayout state)
 */
export function getNormalizedPosition(
  node: Node,
  config: NonGraveyardNodeConfig
): Node['position'] {
  const x = config.xPosition === 0 ? node.position.x : (config.xPosition ?? 0);
  const y = config.yPosition === 0 ? node.position.y : (config.yPosition ?? 0);
  return { x, y };
}

export function transformNonGraveyardNodes(
  node: Node,
  config?: NonGraveyardNodeConfig
): Node | null {
  if (!config) return node;
  // Section group layout masnaged by a different process
  if (node.type !== 'tile') return node;
  if (config.hidden) return null;

  // For group nodes, notes come from visualizationGroups.description (already set in node.data.notes
  // by createWaterfallGraph). We should NOT override with nodeConfigurations.notes to avoid
  // a dual-source-of-truth bug where edits to group description don't persist correctly.
  const isGroupNode = !!(node.data as { isGroupNode?: boolean }).isGroupNode;

  return {
    ...node,
    // Saved groups have an explicit layout; zero is a valid coordinate.
    position: isGroupNode
      ? { x: config.xPosition, y: config.yPosition }
      : getNormalizedPosition(node, config),
    ...(config.height && { height: config.height }),
    ...(config.width && { width: config.width }),
    data: {
      ...node.data,
      ...(config.fontSize && {
        fontSizeVariant: config.fontSize as TileFontSizeVariant,
      }),
      // Only apply config.notes for non-group tiles. Group notes are sourced from
      // visualizationGroups.description to maintain a single source of truth.
      ...(!isGroupNode && config.notes && { notes: config.notes }),
      ...(config.type && {
        componentType: config.type as GraphVizNodeComponentType,
      }),
      ...(config.iconShape && {
        iconShape: parseIconShape(config.iconShape),
      }),
      ...(config.tileEmphasis && {
        tileEmphasis: parseTileEmphasis(config.tileEmphasis),
      }),
      ...(config.dimensionsFinalized && { dimensionsFinalized: true }),
      ...(config.estateWaterfallConfig && {
        estateWaterfallConfig: config.estateWaterfallConfig,
      }),
    },
  };
}

export interface TransformNodesWithViewConfigInput {
  nodes: Node[];
  nodeConfigurations: GraphViz_NodeConfigFragment[];
}

export function transformNodesWithViewConfig({
  nodes,
  nodeConfigurations,
}: TransformNodesWithViewConfigInput): Node[] {
  const viewConfigByNodeId: Record<
    string,
    // We won't always have a 1:1 mapping of view config to nodes if this is the first time initializing the view,
    // or if nodes haven't been saved yet into the config
    GraphViz_NodeConfigFragment | undefined
  > = keyBy(nodeConfigurations, ({ nodeID }) => nodeID);

  const nonGraveyardNodes = nodes.filter((node) => {
    return !isNodeEligibleForGraveyard(node);
  });

  const graveyardNodes: TileNode[] = nodes.filter(isNodeEligibleForGraveyard);

  // Filter out configs without x or y position
  const nonGraveyardNodeConfigs = Object.fromEntries(
    Object.entries(viewConfigByNodeId).reduce(
      (acc, [nodeId, config]) => {
        if (!isNil(config?.xPosition) && !isNil(config?.yPosition)) {
          acc.push([nodeId, config as NonGraveyardNodeConfig]);
        }
        return acc;
      },
      [] as [string, NonGraveyardNodeConfig][]
    )
  );

  const transformedNonGraveyardNodes = nonGraveyardNodes.flatMap((node) => {
    return (
      transformNonGraveyardNodes(node, nonGraveyardNodeConfigs[node.id]) ?? []
    );
  });

  const boundingBoxForNonGraveyardNodes = getBoundingBoxForNodes(
    transformedNonGraveyardNodes
  );

  const componentTypes = new Set(
    transformedNonGraveyardNodes.flatMap((n) =>
      n.type === 'tile' ? n.data.componentType : []
    )
  );

  const defaultComponentType =
    componentTypes.size === 1
      ? componentTypes.values().next().value
      : GraphVizNodeComponentType.Tile;

  const graveyardIdxBySection: Record<string, number> = {
    [AfterDeath.None]: 0,
    [AfterDeath.First]: 0,
    [AfterDeath.Second]: 0,
  };
  const transformedGraveyardNodes = graveyardNodes.flatMap((node) => {
    const graveyardIdx =
      graveyardIdxBySection[node.data.sectionLabelId ?? 'unknown'] ?? 0;

    const transformedGraveyardNode = transformNodeForNodeGraveyard(
      node,
      graveyardIdx,
      boundingBoxForNonGraveyardNodes.left, // First, graveyard nodes line up with the left of the non-graveyard nodes,
      viewConfigByNodeId[node.id]
    );
    const sectionId = node.data?.sectionLabelId ?? 'unknown';
    if (graveyardIdxBySection[sectionId] !== undefined) {
      graveyardIdxBySection[sectionId] += 1;
    }
    return transformedGraveyardNode;
  });

  const boundingBoxForGraveyardNodes = getBoundingBoxForNodes(
    transformedGraveyardNodes
  );

  const widthOfGraveyardNodes =
    boundingBoxForGraveyardNodes.right - boundingBoxForGraveyardNodes.left;

  const widthOfNonGraveyardNodes =
    boundingBoxForNonGraveyardNodes.right -
    boundingBoxForNonGraveyardNodes.left;

  // Get center of non-graveyard nodes
  const sectionCenter =
    boundingBoxForNonGraveyardNodes.left + widthOfNonGraveyardNodes / 2;

  // Shift graveyard nodes to the center of the section
  const transformedGraveyardNodesCentered = transformedGraveyardNodes.map(
    (node): TileNode => {
      return {
        ...node,
        data: {
          ...node.data,
          componentType: node.data.componentType ?? defaultComponentType,
        },
        position: {
          ...node.position,
          x: node.position.x + sectionCenter - widthOfGraveyardNodes / 2, // Second, take the current node x position, shift it to the right to be indexed on the section center, then shift it back to the left by half the width of the graveyard nodes
        },
      };
    }
  );

  // Reconstitute text note nodes from saved configurations.
  // Text notes are not generated from the graph data, only from saved configs.
  const textNoteNodes: Node[] = getTextNoteConfigs(nodeConfigurations).map(
    createTextNoteNodeFromConfig
  );

  return [
    ...transformedGraveyardNodesCentered,
    ...transformedNonGraveyardNodes,
    ...textNoteNodes,
  ];
}
